import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { QRCodeSVG } from 'qrcode.react';
import { 
  ShieldCheck, 
  ShieldX,
  Search,
  Loader2,
  Globe,
  X,
  Maximize2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fullscreen Certificate Modal Component for Public Verification
const PublicCertificateModal = ({ result, verificationUrl, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/95 overflow-auto">
      <button 
        onClick={onClose}
        className="fixed top-4 right-4 text-white/80 hover:text-white z-50 bg-black/50 rounded-full p-2"
        data-testid="close-verify-modal"
      >
        <X className="h-6 w-6" />
      </button>
      
      <div className="min-h-full flex flex-col items-center justify-start p-4 pt-16 pb-8">
        <div className="text-center text-white/80 text-xs mb-4">
          Pinch to zoom • Rotate for best view
        </div>
        
        <div 
          className="w-full max-w-2xl touch-manipulation"
          style={{ touchAction: 'pinch-zoom pan-x pan-y' }}
        >
          <div 
            className="relative overflow-hidden rounded-lg shadow-2xl bg-[#0A1628] w-full"
            style={{ aspectRatio: '1.414/1' }}
          >
            <img 
              src="/certificate-bg.png" 
              alt="Certificate Background"
              className="absolute inset-0 w-full h-full object-cover"
            />
            
            <div className="absolute inset-0 flex flex-col items-center text-center px-[8%] pt-[26%]">
              <h3 className="text-[#C9A227] text-[3.5vw] sm:text-lg md:text-xl font-bold tracking-wide mb-1">
                {result.member_name}
              </h3>
              
              <p className="text-white text-[2vw] sm:text-[10px] md:text-xs max-w-[90%] leading-relaxed">
                has been admitted as a <span className="text-[#C9A227] font-semibold uppercase">{result.membership_type}</span> MEMBER of UIEHEC,
                in recognition of their commitment to international cooperation,
                academic excellence, and the advancement of higher education.
              </p>
              
              <p className="text-white/70 text-[1.8vw] sm:text-[9px] md:text-[10px] mt-1 max-w-[85%]">
                Membership is effective from {result.valid_from || result.issue_date} and remains valid until {result.valid_until || 'Indefinite'},
                subject to the UIEHEC Charter and internal regulations.
              </p>
              
              <div className="border border-[#C9A227]/50 rounded flex divide-x divide-[#C9A227]/30 mt-2 max-w-[85%] w-full">
                <div className="flex-1 p-1 text-center">
                  <p className="text-[#C9A227] font-semibold text-[1.5vw] sm:text-[8px]">Certificate No:</p>
                  <p className="text-white font-mono text-[1.8vw] sm:text-[9px]">{result.certificate_number}</p>
                </div>
                <div className="flex-1 p-1 text-center">
                  <p className="text-[#C9A227] font-semibold text-[1.5vw] sm:text-[8px]">Type:</p>
                  <p className="text-white uppercase text-[1.8vw] sm:text-[9px]">{result.membership_type}</p>
                </div>
                <div className="flex-1 p-1 text-center">
                  <p className="text-[#C9A227] font-semibold text-[1.5vw] sm:text-[8px]">Issue Date:</p>
                  <p className="text-white text-[1.8vw] sm:text-[9px]">{result.issue_date}</p>
                </div>
              </div>
            </div>

            {verificationUrl && (
              <div className="absolute bottom-[8%] right-[5%] bg-white p-0.5 rounded shadow-lg">
                <QRCodeSVG 
                  value={verificationUrl}
                  size={25}
                  level="M"
                />
              </div>
            )}

            <div className={`absolute top-1 right-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold ${
              result.status === 'active' 
                ? 'bg-green-500 text-white' 
                : 'bg-red-500 text-white'
            }`}>
              {result.status?.toUpperCase()}
            </div>
          </div>
        </div>

        {/* Certificate Details Below */}
        <div className="w-full max-w-2xl mt-4 grid grid-cols-3 gap-2 text-center">
          <div className="bg-white/10 rounded p-2">
            <p className="text-white/60 text-[10px]">Certificate</p>
            <p className="text-white text-xs font-mono">{result.certificate_number}</p>
          </div>
          <div className="bg-white/10 rounded p-2">
            <p className="text-white/60 text-[10px]">Type</p>
            <p className="text-white text-xs uppercase">{result.membership_type}</p>
          </div>
          <div className="bg-white/10 rounded p-2">
            <p className="text-white/60 text-[10px]">Status</p>
            <p className="text-green-400 text-xs uppercase">{result.status}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function VerifyPage() {
  const { certificateNumber: urlCertNumber } = useParams();
  const [certificateNumber, setCertificateNumber] = useState(urlCertNumber || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showFullscreen, setShowFullscreen] = useState(false);

  useEffect(() => {
    if (urlCertNumber) {
      handleVerify(urlCertNumber);
    }
  }, [urlCertNumber]);

  const handleVerify = async (certNum = certificateNumber) => {
    if (!certNum.trim()) return;
    
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await axios.get(`${API}/certificates/verify/${certNum.trim()}`);
      setResult(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Certificate not found. Please check the certificate number and try again.');
      } else {
        setError('An error occurred while verifying the certificate.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleVerify();
  };

  const verificationUrl = `${window.location.origin}/verify/${result?.certificate_number || ''}`;

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-[#0E285B] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-4">
              <ShieldCheck className="h-6 w-6 text-[#F5A623]" />
              <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
                Official Verification
              </span>
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-white leading-tight mb-4">
              Verify Certificate
            </h1>
            <p className="text-slate-300">
              Verify the authenticity of any UIEHEC membership certificate
            </p>
          </div>
        </div>
      </section>

      {/* Verification Section */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search Form */}
          <div className="bg-white p-6 rounded-sm shadow-sm border border-slate-100 mb-8">
            <form onSubmit={handleSubmit} className="flex gap-3">
              <Input
                type="text"
                value={certificateNumber}
                onChange={(e) => setCertificateNumber(e.target.value)}
                placeholder="Enter Certificate Number (e.g., UIEHEC-2026-TEST001)"
                className="flex-1 h-12 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                data-testid="certificate-number-input"
              />
              <Button 
                type="submit"
                className="bg-[#0E285B] text-white hover:bg-[#0E285B]/90 h-12 px-8"
                disabled={loading}
                data-testid="verify-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Verify
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Certificate Display with Template Background */}
          {result && result.valid && (
            <div className="space-y-6" data-testid="certificate-display">
              {/* Fullscreen Modal */}
              {showFullscreen && (
                <PublicCertificateModal 
                  result={result} 
                  verificationUrl={verificationUrl} 
                  onClose={() => setShowFullscreen(false)} 
                />
              )}

              {/* Verification Success Badge */}
              <div className="flex items-center justify-center gap-4 bg-green-50 border border-green-200 rounded-sm p-4">
                <div className="h-12 w-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-green-800">Certificate Verified</p>
                  <p className="text-sm text-green-600">This certificate is authentic and currently active</p>
                </div>
              </div>

              {/* Mobile View - Simplified Card */}
              <div className="lg:hidden">
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                  {/* Certificate Preview Thumbnail */}
                  <div 
                    className="relative bg-[#0A1628] p-6 cursor-pointer"
                    onClick={() => setShowFullscreen(true)}
                    data-testid="verify-mobile-preview"
                  >
                    <div className="flex items-center justify-center gap-4">
                      <ShieldCheck className="h-12 w-12 text-[#C9A227]" />
                      <div className="text-left">
                        <h3 className="text-white font-semibold text-lg">{result.member_name}</h3>
                        <p className="text-[#C9A227] text-sm uppercase">{result.membership_type} Member</p>
                      </div>
                    </div>
                    <div className="absolute top-3 right-3 bg-white/10 rounded-full p-2">
                      <Maximize2 className="h-5 w-5 text-white" />
                    </div>
                    <div className={`absolute top-3 left-3 px-2 py-0.5 rounded-full text-xs font-bold ${
                      result.status === 'active' 
                        ? 'bg-green-500 text-white' 
                        : 'bg-red-500 text-white'
                    }`}>
                      {result.status?.toUpperCase()}
                    </div>
                  </div>

                  {/* Mobile Info */}
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500">Certificate No:</span>
                      <span className="font-mono text-[#0E285B]">{result.certificate_number}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500">Issue Date:</span>
                      <span className="text-[#0E285B]">{result.issue_date}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500">Membership Type:</span>
                      <span className="text-[#0E285B] uppercase">{result.membership_type}</span>
                    </div>
                  </div>

                  {/* View Full Button */}
                  <div className="p-4 pt-0">
                    <Button 
                      onClick={() => setShowFullscreen(true)}
                      className="w-full bg-[#0E285B] text-white"
                    >
                      <Maximize2 className="mr-2 h-4 w-4" />
                      View Full Certificate
                    </Button>
                  </div>

                  {/* QR Code Section - Mobile */}
                  <div className="p-4 bg-slate-50 border-t flex items-center justify-center gap-4">
                    <div className="bg-white p-2 rounded border">
                      <QRCodeSVG value={verificationUrl} size={60} level="M" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-[#0E285B]">Scan to Verify</p>
                      <p className="text-xs text-slate-500">Share this QR code</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop View - Full Certificate Preview */}
              <div className="hidden lg:block">
                <div 
                  className="relative overflow-hidden rounded-lg shadow-2xl bg-[#0A1628] mx-auto cursor-pointer"
                  style={{ aspectRatio: '1.414/1', maxWidth: '800px' }}
                  onClick={() => setShowFullscreen(true)}
                >
                  {/* Template Background Image */}
                  <img 
                    src="/certificate-bg.png" 
                    alt="Certificate Background"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  
                  {/* Dynamic Content Overlay - Member name, statement and details */}
                  <div className="absolute inset-0 flex flex-col items-center text-center px-8 md:px-16 pt-[26%]">
                    {/* Member Name */}
                    <h3 className="text-[#C9A227] text-lg md:text-2xl font-bold tracking-wide mb-1">
                      {result.member_name}
                    </h3>
                    
                    {/* Membership Statement */}
                    <p className="text-white text-[9px] md:text-xs max-w-lg leading-relaxed">
                      has been admitted as a <span className="text-[#C9A227] font-semibold uppercase">{result.membership_type}</span> MEMBER of UIEHEC,
                      in recognition of their commitment to international cooperation,
                      academic excellence, and the advancement of higher education.
                    </p>
                    
                    {/* Validity - smaller and closer */}
                    <p className="text-white/70 text-[8px] md:text-[10px] mt-2 max-w-md">
                      Membership is effective from {result.valid_from || result.issue_date} and remains valid until {result.valid_until || 'Indefinite'},
                      subject to the UIEHEC Charter and internal regulations.
                    </p>
                    
                    {/* Certificate Details Box - positioned higher to avoid seal */}
                    <div className="border border-[#C9A227]/50 rounded flex divide-x divide-[#C9A227]/30 text-[9px] md:text-xs mt-3 max-w-md w-full">
                      <div className="flex-1 p-1.5 md:p-2 text-center">
                        <p className="text-[#C9A227] font-semibold text-[8px] md:text-[10px]">Certificate No:</p>
                        <p className="text-white font-mono">{result.certificate_number}</p>
                      </div>
                      <div className="flex-1 p-1.5 md:p-2 text-center">
                        <p className="text-[#C9A227] font-semibold text-[8px] md:text-[10px]">Membership Type:</p>
                        <p className="text-white uppercase">{result.membership_type}</p>
                      </div>
                      <div className="flex-1 p-1.5 md:p-2 text-center">
                        <p className="text-[#C9A627] font-semibold text-[8px] md:text-[10px]">Issue Date:</p>
                        <p className="text-white">{result.issue_date}</p>
                      </div>
                    </div>
                  </div>

                  {/* QR Code - Bottom Right corner in empty space */}
                  <div className="absolute bottom-[8%] right-[5%] bg-white p-1 rounded shadow-lg">
                    <QRCodeSVG 
                      value={verificationUrl}
                      size={32}
                      level="M"
                    />
                  </div>

                  {/* Status Badge */}
                  <div className="absolute top-3 right-3 md:top-4 md:right-4 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-bold bg-green-500 text-white">
                    {result.status?.toUpperCase()}
                  </div>

                  {/* Fullscreen hint */}
                  <div className="absolute bottom-4 left-4 bg-black/50 rounded-full px-3 py-1 text-white text-xs flex items-center gap-1">
                    <Maximize2 className="h-3 w-3" />
                    Click to enlarge
                  </div>
                </div>
              </div>

              {/* Certificate Info Cards */}
              <div className="hidden lg:grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
                <div className="bg-white p-4 rounded-sm border border-slate-100 text-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Certificate Number</p>
                  <p className="font-mono text-[#0E285B] font-semibold">{result.certificate_number}</p>
                </div>
                <div className="bg-white p-4 rounded-sm border border-slate-100 text-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Issue Date</p>
                  <p className="text-[#0E285B] font-semibold">{result.issue_date}</p>
                </div>
                <div className="bg-white p-4 rounded-sm border border-slate-100 text-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Status</p>
                  <p className="text-green-600 font-semibold uppercase">{result.status}</p>
                </div>
              </div>
            </div>
          )}

          {/* Invalid Certificate */}
          {result && !result.valid && (
            <div className="bg-red-50 border-2 border-red-200 p-8 rounded-sm text-center" data-testid="certificate-invalid">
              <div className="h-16 w-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldX className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-bold text-red-800 mb-2">
                Certificate Invalid or Inactive
              </h3>
              <p className="text-red-600">
                This certificate is not valid or has been revoked.
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 p-6 rounded-sm" data-testid="verification-error">
              <div className="flex items-center gap-4">
                <ShieldX className="h-10 w-10 text-red-500 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-red-800">Certificate Not Found</h3>
                  <p className="text-red-600">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Info */}
          {!result && !error && (
            <div className="text-center text-slate-500 py-8">
              <Globe className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>Enter a certificate number above to verify its authenticity</p>
              <p className="text-sm mt-2">
                Certificate numbers follow the format: <span className="font-mono">UIEHEC-YYYY-XXXXXX</span>
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
